/**
 * The studio confirmation service: its tariff, its target, and the vocabulary
 * a case is read in.
 *
 * EVERY FIGURE AND EVERY RULE HERE IS PROVISIONAL, and that is not a hedge. The
 * owner confirmed the $250 amount in the OND-70 review and then delegated the
 * remaining service rules to the implementing agent "as initial defaults, to be
 * revised later … Keep them configurable and documented; development can proceed
 * and later tickets can revise them." So the two figures below are the
 * configuration point on this side, and `ondo.attribution_tariff()` and
 * `ondo.attribution_service_target_days()` are the configuration point on the
 * other.
 *
 * MIRRORS THOSE TWO FUNCTIONS BY NAME, and `rls.sh` reads these constants out of
 * this file's source and asserts the pair agree — the arrangement
 * `registrationFee.ts` and `20260910110000` decision 4 set up, copied
 * deliberately. The SQL is the record: the fee row is written by trigger from
 * that function and never from here. This copy exists so a screen can say what
 * the service costs and when it is due before anybody commits to it.
 *
 * A third copy anywhere is a drift waiting to happen.
 */
export const ATTRIBUTION_FEE_MINOR = 25_000;
export const ATTRIBUTION_FEE_CURRENCY = "USD";
/**
 * How long the studio's target is, in calendar days from the ask.
 *
 * IT IS A TARGET AND NOT A GUARANTEE OF AUTHENTICATION, which is a product
 * sentence as much as a technical one. Nothing in the schema acts when it
 * passes: no expiry, no reminder, no charge, no answer. A case whose target has
 * gone by reads `overdue` and stays exactly as pending as it was — OND-87
 * forbids inventing expiry or reminder policy, and OND-60 says in terms "do not
 * automatically authenticate, reject or charge".
 */
export const ATTRIBUTION_SERVICE_TARGET_DAYS = 30;
/**
 * What has happened to a case short of an answer.
 *
 * Mirrors `ondo.attribution_service_step`. Nothing enforces that but this
 * comment and `pnpm test:rls`.
 *
 * `target` is the only one no person chooses: a trigger writes it when the ask
 * is made. The other two are the studio's, and NEITHER ENDS THE REQUEST —
 * asking for more evidence and revising the date are both things that happen
 * while the question is still open, which is why neither is a
 * `VerificationRequestOutcome`.
 */
export const ATTRIBUTION_SERVICE_STEPS = [
    "target",
    "evidence_requested",
    "extension",
];
export function isAttributionServiceStep(value) {
    return (typeof value === "string" &&
        ATTRIBUTION_SERVICE_STEPS.includes(value));
}
/**
 * Where a case stands. Mirrors `ondo.attribution_service_stage`.
 *
 * EIGHT VALUES, NOT FOUR, AND THE GAP IS THE POINT. OND-60 names four
 * outcomes — "confirmed attribution, rejected attribution, more evidence
 * needed, and declined opinion" — and the shared request flow already produced
 * four more states that a service screen must not collapse into them:
 *
 * - `awaiting` — asked, and nothing said. Most cases, most of the time.
 * - `no_recall` — the studio looked and does not remember. NOT a decline:
 *   `20260808085900` refused to file it as one, because "a registry that forced
 *   it into 'declined' would be recording a shrug as a rebuff".
 * - `referred` — passed to somebody else, who now holds a case of their own.
 * - `cancelled` — the asker withdrew before an answer.
 *
 * `20261004096000` recorded what dropping them costs: "a screen reading the
 * state alone would tell a collector that nobody had been asked when a studio
 * had been asked and declined to answer."
 *
 * NOT the same thing as `AttributionState`, which answers a different question —
 * what an authority has said about the WORK, over every claim on it and every
 * unasked position. A work can read `confirmed` while a case on it reads
 * `declined`, because somebody else confirmed it.
 *
 * Derived on every read and stored nowhere, which is `ondo.works.verification`'s
 * epitaph applied: "A column nothing can move is not a fact about a work, it is
 * a claim the registry has stopped being able to make."
 */
export const ATTRIBUTION_SERVICE_STAGES = [
    "awaiting",
    "evidence_requested",
    "confirmed",
    "rejected",
    "declined",
    "no_recall",
    "referred",
    "cancelled",
];
export function isAttributionServiceStage(value) {
    return (typeof value === "string" &&
        ATTRIBUTION_SERVICE_STAGES.includes(value));
}
/** The four OND-60 named, as against the four the shared flow already had. */
const NAMED_OUTCOMES = [
    "confirmed",
    "rejected",
    "evidence_requested",
    "declined",
];
/**
 * Whether this stage is one of the four outcomes the ticket distinguishes.
 *
 * Useful for exactly one thing and worth having for it: a screen that wants to
 * say "the studio has answered" must not count `awaiting`, `cancelled` or
 * `referred`, and must not count `evidence_requested` as an ANSWER either even
 * though it is one of the four — which is what `isSettledStage` below is for.
 */
export function isNamedOutcome(stage) {
    return NAMED_OUTCOMES.includes(stage);
}
/**
 * Whether the question is over.
 *
 * `awaiting` and `evidence_requested` are both LIVE: the second is the studio
 * asking for more before it answers, and OND-60's "No automatic refund merely
 * because more evidence is requested before the answer" is the sentence that
 * settles it.
 */
export function isSettledStage(stage) {
    return stage !== "awaiting" && stage !== "evidence_requested";
}
/**
 * Whether this stage is one the studio is paid for.
 *
 * MIRRORS `ondo.raise_attribution_fee()`'s outcome test and is NOT the thing
 * that decides it — the trigger is, inside the answering transaction. This
 * exists so a screen can explain the charge it is already showing rather than
 * compute one.
 *
 * A rejection costs what a confirmation costs: OND-60 says "Either a positive
 * attribution confirmation or an explicit rejection is a substantive answer;
 * disagreement with the result is not a refund reason", and a studio paid only
 * for a yes would be a studio with a reason to say yes.
 */
export function isChargeableStage(stage) {
    return stage === "confirmed" || stage === "rejected";
}
/**
 * How a fee that should not stand as written is corrected.
 *
 * Mirrors `ondo.attribution_fee_correction`. ONDO neither collects nor returns
 * money, so a `void` is an admission, a `refund_due` is the studio writing down
 * that it owes one, and a `refund_recorded` is a note about a payment made
 * somewhere else — with its external reference, because "recorded honestly"
 * means recording what it was.
 */
export const ATTRIBUTION_FEE_CORRECTIONS = [
    "void",
    "refund_due",
    "refund_recorded",
];
export function isAttributionFeeCorrection(value) {
    return (typeof value === "string" &&
        ATTRIBUTION_FEE_CORRECTIONS.includes(value));
}
/**
 * What a fee is standing at, derived from the fee and its newest correction.
 *
 * Mirrors nothing in Postgres: `ondo.attribution_service_cases()` computes it as
 * text, because it is a reading of two tables rather than a value either one
 * holds. No column stores it and the fee row is never rewritten — OND-60:
 * "Preserve the original fee and any recorded payment."
 */
export const ATTRIBUTION_FEE_STANDINGS = [
    "due",
    "recorded_paid",
    "void",
    "refund_due",
    "refunded",
];
export function isAttributionFeeStanding(value) {
    return (typeof value === "string" &&
        ATTRIBUTION_FEE_STANDINGS.includes(value));
}
/** Whether anything is still owed on this fee. */
export function isOwed(standing) {
    return standing === "due";
}
//# sourceMappingURL=attributionFee.js.map